"use client";

import { useState, useMemo, useEffect, useCallback } from "react";
import { formOptions } from "@tanstack/react-form";
import { useAppForm } from "./form";
import { useTranslations } from "next-intl";
import { usePreviousPath } from "@/app/_providers/PreviousPathProvider";
import { getUTMs } from "@/app/_analytics/utmHandler";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { toast } from "@/app/_providers/toast";
import { submitContactSales, ContactSalesError, ContactSalesErrorType } from "@/app/_api/contactSales";
import type { ContactSalesSubmissionPayload, ContactSalesFormValues } from "../shared/types";
import { CONTACT_SALES_FORM_DEFAULTS } from "../shared/constants";
import {
  createValidationMessages,
  loadFormFromLocalStorage,
  saveFormToLocalStorage,
  clearFormFromLocalStorage,
  isPersonalEmail,
  getAISDRUrl,
  getGeoDefaultCountry,
} from "../shared/utils";
import { generateId } from "@utilities/generateId";
import { getErrorMessageFromUnknownError } from "@utilities/errors";
import { logger } from "@utilities/logger";
import { createFieldValidators } from "../shared/validators";
import { Step1 } from "../steps/Step1";
import { Step2 } from "../steps/Step2";
import { FinalScreen } from "../steps/FinalScreen";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { trackContactSalesFormDreamdata } from "@/app/(pages)/[locale]/AnalyticsComponent";

const formOpts = formOptions({
  defaultValues: CONTACT_SALES_FORM_DEFAULTS,
});

const STEP_FIELDS: Record<number, (keyof ContactSalesFormValues)[]> = {
  1: ["website", "email"],
  2: [
    "first_name",
    "last_name",
    "company",
    "company_size",
    "industry",
    "expected_monthly_usage",
    "country",
    "phone_number",
    "detail",
    "how_did_you_hear",
    "platform_interest",
  ],
};

export function useContactSalesFormV2() {
  const [step, setStep] = useState<number>(1);
  const [isInitialized, setIsInitialized] = useState(false);
  const [savedFormData, setSavedFormData] = useState<{ step: number; formData: ContactSalesFormValues } | null>(null);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [submissionId, setSubmissionId] = useState<string>("");
  const t = useTranslations("ClientKeys.contact_sales_page.form");
  const tValidation = useTranslations("ClientKeys.forms.validation");
  const previousPath = usePreviousPath();

  const validationMessages = useMemo(() => createValidationMessages(tValidation), [tValidation]);
  const validators = useMemo(() => createFieldValidators(validationMessages), [validationMessages]);

  const form = useAppForm({
    ...formOpts,
    onSubmit: async ({ value }) => {
      // Capture event properties early (before async work) to reflect trigger-time state
      const referrer = previousPath?.current || (typeof document !== "undefined" ? document.referrer : "");

      let token: string;

      try {
        token = await getCaptchaToken({ behaviourType: "contact_sales" });
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "Failed to get captcha token");
        toast.error(t("errors.captcha_failed"));

        // Track captcha failure
        captureEvent(EventName.mw_contact_sales_v2_submission_failed, {
          cs_v2_error_type: "captcha_error",
          cs_v2_error_message: getErrorMessageFromUnknownError(error),
          cs_v2_is_personal_email: isPersonalEmail(value.email),
          cs_v2_contact_sales_page_referrer: referrer,
        });

        throw error;
      }

      try {
        const utms = getUTMs();
        const submissionId = generateId();
        const submissionPayload: ContactSalesSubmissionPayload = {
          ...value,
          submission_id: submissionId,
          utm_source: utms.utmSource,
          utm_medium: utms.utmMedium,
          utm_campaign: utms.utmCampaign,
          utm_content: utms.utmContent,
          utm_term: utms.utmTerm,
          gclid: utms.gclid,
          contact_sales_page_referrer: referrer,
        };

        await submitContactSales(submissionPayload, token);

        const personalEmail = isPersonalEmail(value.email);
        const isAiSdrEligible = !!getAISDRUrl(
          value.country,
          value.company,
          value.first_name,
          value.email,
          submissionId,
        );

        // Track submission success
        captureEvent(EventName.mw_contact_sales_v2_submission_succeeded, {
          cs_v2_submission_id: submissionId,
          cs_v2_is_personal_email: personalEmail,
          cs_v2_country: value.country,
          cs_v2_is_ai_sdr_eligible: isAiSdrEligible,
          cs_v2_contact_sales_page_referrer: referrer,
          cs_v2_company: value.company,
          cs_v2_email: value.email,
          cs_v2_expected_monthly_usage: value.expected_monthly_usage,
          cs_v2_platform_interest: value.platform_interest || undefined,
          cs_v2_hdyh_variant: "text",
          csf_form_version: "v2",
        });

        // Fire legacy contact_sales_event for Page2 (parity with original form)
        captureEvent(EventName.contact_sales_event, {
          state: "2-eligibility-loading",
          contact_form_data: {
            company: value.company,
            detail: value.detail,
            email: value.email,
            expected_monthly_usage: value.expected_monthly_usage,
            first_name: value.first_name,
            last_name: value.last_name,
            industry: value.industry,
            phone_number: value.phone_number,
            region: value.country, // V2 uses 'country' instead of 'region'
            website: value.website,
          },
          page: "Page2",
          contact_sales_page_referrer: referrer,
          csf_form_version: "v2",
          csf_submission_id: submissionId,
        });

        setSubmissionId(submissionId);
        clearFormFromLocalStorage();
        setStep(3);
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "Failed to submit contact sales form");

        let errorType: string = ContactSalesErrorType.UNKNOWN_ERROR;

        if (error instanceof ContactSalesError) {
          errorType = error.type;
          // Handle specific error types with appropriate messages
          switch (error.type) {
            case ContactSalesErrorType.VALIDATION_ERROR:
              toast.error(t("errors.validation_failed"));
              break;
            case ContactSalesErrorType.CAPTCHA_ERROR:
              toast.error(t("errors.captcha_failed"));
              break;
            case ContactSalesErrorType.RATE_LIMIT_ERROR:
              toast.error(t("errors.rate_limit"));
              break;
            case ContactSalesErrorType.UNKNOWN_ERROR:
              toast.error(t("errors.unknown_error"));
              break;
            case ContactSalesErrorType.SERVER_ERROR:
              toast.error(t("errors.server_error"));
              break;
            default:
              toast.error(t("errors.submission_failed"));
          }
        } else {
          // Fallback for unexpected errors
          toast.error(t("errors.submission_failed"));
        }

        // Track submission failure
        captureEvent(EventName.mw_contact_sales_v2_submission_failed, {
          cs_v2_error_type: errorType,
          cs_v2_error_message: getErrorMessageFromUnknownError(error),
          cs_v2_is_personal_email: isPersonalEmail(value.email),
          cs_v2_contact_sales_page_referrer: referrer,
        });

        throw error;
      }
    },
  });

  useEffect(() => {
    if (isInitialized) return;

    const savedData = loadFormFromLocalStorage();
    const geoCountry = getGeoDefaultCountry();
    if (geoCountry) {
      form.setFieldValue("country", geoCountry);
    }

    if (savedData) {
      setSavedFormData(savedData);
      setShowResumePrompt(true);
    }

    captureEvent(EventName.mw_contact_sales_v2_form_viewed, {
      cs_v2_has_saved_progress: !!savedData,
      cs_v2_contact_sales_page_referrer:
        previousPath?.current || (typeof document !== "undefined" ? document.referrer : ""),
    });

    setIsInitialized(true);
  }, [isInitialized, previousPath]);

  const validateStepFields = async (stepNum: number): Promise<boolean> => {
    const fieldsToValidate = STEP_FIELDS[stepNum];

    if (fieldsToValidate.length === 0) {
      return true;
    }

    await Promise.all(fieldsToValidate.map(fieldName => form.validateField(fieldName, "change")));

    const hasErrors = fieldsToValidate.some(fieldName => {
      const fieldState = form.getFieldMeta(fieldName);
      return fieldState?.errors && fieldState.errors.length > 0;
    });

    return !hasErrors;
  };

  const getStepFields = (stepNum: number) => STEP_FIELDS[stepNum];

  const moveToNextStep = useCallback(async (): Promise<void> => {
    if (step === 3) return; // Already at final screen

    const isValid = await validateStepFields(step);

    if (isValid) {
      const currentFormData = form.state.values;
      saveFormToLocalStorage(step, currentFormData);

      if (step === 1) {
        trackContactSalesFormDreamdata(currentFormData.email);

        // Fire legacy contact_sales_event for Page1 (parity with original form)
        captureEvent(EventName.contact_sales_event, {
          state: "1-form-ready",
          contact_form_data: {
            company: "", // Company is collected in step 2 in V2
            email: currentFormData.email,
          },
          page: "Page1",
          contact_sales_page_referrer:
            previousPath?.current || (typeof document !== "undefined" ? document.referrer : ""),
          csf_form_version: "v2",
        });
      }

      // Track step completion
      captureEvent(EventName.mw_contact_sales_v2_step_completed, {
        cs_v2_step_number: step,
        cs_v2_is_personal_email: isPersonalEmail(currentFormData.email),
        cs_v2_contact_sales_page_referrer:
          previousPath?.current || (typeof document !== "undefined" ? document.referrer : ""),
      });

      if (showResumePrompt) {
        setShowResumePrompt(false);
      }

      setStep(prev => prev + 1);
    }
  }, [step, form, showResumePrompt, previousPath]);

  const back = useCallback(() => {
    if (step > 1) {
      captureEvent(EventName.mw_contact_sales_v2_back_clicked, {
        cs_v2_step_number: step,
      });
      setStep(prev => prev - 1);
    }
  }, [step]);

  const reset = useCallback(() => {
    form.reset();
    setStep(1);
    clearFormFromLocalStorage();
  }, [form]);

  const continueWithSavedForm = () => {
    if (!savedFormData) return;

    Object.entries(savedFormData.formData).forEach(([key, value]) => {
      if (value) {
        form.setFieldValue(key as keyof ContactSalesFormValues, value);
      }
    });

    setStep(savedFormData.step);
    setShowResumePrompt(false);
    setSavedFormData(null);
  };

  const startFreshForm = () => {
    clearFormFromLocalStorage();
    setShowResumePrompt(false);
    setSavedFormData(null);

    const geoCountry = getGeoDefaultCountry();
    if (geoCountry) {
      form.setFieldValue("country", geoCountry);
    }
  };

  const stepIndex = step - 1;

  const steps = useMemo(
    () => [
      {
        title: t("steps.step1.title"),
        subtitle: t("steps.step1.subtitle"),
        component: <Step1 key='step1' form={form} validators={validators} t={t} />,
        navigation: {
          showProgressBar: true,
          buttons: [
            {
              type: "next" as const,
              label: t("buttons.next"),
              variant: "primary" as const,
              validateFields: STEP_FIELDS[1],
              onClick: moveToNextStep,
            },
          ],
        },
      },
      {
        title: t("steps.step2.title"),
        subtitle: t("steps.step2.subtitle"),
        component: <Step2 key='step2' form={form} validators={validators} t={t} />,
        navigation: {
          showProgressBar: true,
          buttons: [
            {
              type: "back" as const,
              label: t("buttons.go_back"),
              variant: "secondary" as const,
              onClick: back,
            },
            {
              type: "submit" as const,
              label: t("buttons.submit"),
              variant: "primary" as const,
            },
          ],
        },
      },
      {
        title: t("thank_you_title"),
        subtitle: t("thank_you_message"),
        component: <FinalScreen key='step3' reset={reset} formData={form.state.values} submissionId={submissionId} />,
        navigation: null,
      },
    ],
    [t, form, validators, reset, moveToNextStep, back, submissionId],
  );

  return {
    form,
    step,
    stepIndex,
    showResumePrompt,
    continueWithSavedForm,
    startFreshForm,
    steps,
    validators,
    t,
    getStepFields,
    moveToNextStep,
    back,
    reset,
  };
}

export type UseContactSalesFormV2Return = ReturnType<typeof useContactSalesFormV2>;
